/**
 * An article's house format, as fields instead of pseudo-markup.
 *
 * Articles are stored as one markdown body with fixed headings (the drafter
 * and the AI review agree on them: `core/kb/normalise.ts`). Authors were
 * asked to type those headings themselves into one box, and the editor felt
 * muddled — so the editor now has a box per section, and this splits a body
 * into them and joins them back.
 *
 * TWO FORMATS. Troubleshooting — a problem, and the actions that fix it —
 * is the original. Information is for the rest: a policy, where to find
 * something, the status of something ("the new handbook is being finished;
 * HR will email everyone when it is out"). Forcing those into Symptom and
 * Steps made authors invent steps for things that have none. Which one an
 * article is, is read from its headings (`formatOf`), so there is nothing
 * else to store and nothing that reads a body has to change.
 *
 * NOTHING IS LOST. Text before the first heading, and any section under a
 * heading that is not one of these, comes back as `other` and is written
 * after the known sections, headings and all.
 */

export type ArticleFormat = 'troubleshooting' | 'information';

/** Troubleshooting: the original house format. */
export const ARTICLE_SECTIONS = [
  { key: 'symptom', heading: 'Symptom', hint: 'What the person sees, in their words.' },
  { key: 'appliesTo', heading: 'Applies to', hint: 'Systems, conditions, who this is and is not for.' },
  { key: 'steps', heading: 'Steps', hint: 'One action each, in order.' },
  { key: 'check', heading: 'Check it worked', hint: 'How they can tell it is fixed.' },
  { key: 'escalation', heading: 'If that did not work', hint: 'What to do next.' },
] as const;

/** Information: a question and its answer. No steps. */
export const INFORMATION_SECTIONS = [
  { key: 'question', heading: 'Question', hint: 'What people ask, in their words.' },
  { key: 'appliesTo', heading: 'Applies to', hint: 'Who this is for, and the ways people ask for it.' },
  { key: 'answer', heading: 'Answer', hint: 'The answer, in plain sentences. No steps needed.' },
  { key: 'more', heading: 'More information', hint: 'Links, who to contact, when this will change.' },
] as const;

export function sectionsFor(format: ArticleFormat) {
  return format === 'information' ? INFORMATION_SECTIONS : ARTICLE_SECTIONS;
}

/** The headings of each format, in house order. */
export const FORMAT_HEADINGS: Readonly<Record<ArticleFormat, readonly string[]>> = {
  troubleshooting: ARTICLE_SECTIONS.map((s) => s.heading),
  information: INFORMATION_SECTIONS.map((s) => s.heading),
};

/** Headings only Information uses: their presence is what marks an article as one. */
const INFORMATION_ONLY = ['question', 'answer', 'more information'];

/** Which format a body is in: Information when it carries any heading only Information has. */
export function formatOf(body: string): ArticleFormat {
  for (const m of (body ?? '').matchAll(/^#{1,6}\s+(.+?)\s*#*\s*$/gm)) {
    if (INFORMATION_ONLY.includes(m[1]!.trim().toLowerCase())) return 'information';
  }
  return 'troubleshooting';
}

export interface ArticleParts {
  format: ArticleFormat;
  symptom: string;
  appliesTo: string;
  steps: string[];
  check: string;
  escalation: string;
  question: string;
  answer: string;
  more: string;
  /** Anything outside the known sections, kept as written. */
  other: string;
}

type TextKey = Exclude<keyof ArticleParts, 'steps' | 'other' | 'format'>;
type SectionKey = TextKey | 'steps';

export function emptyParts(format: ArticleFormat = 'troubleshooting'): ArticleParts {
  return { format, symptom: '', appliesTo: '', steps: [], check: '', escalation: '', question: '', answer: '', more: '', other: '' };
}

/** A numbered or bulleted line, marker removed; null for anything else. */
function stepText(line: string): string | null {
  const m = /^\s*(?:\d+[.)]|[-*•])\s+(.*)$/.exec(line);
  return m ? m[1]!.trim() : null;
}

/** Steps from a section's text: one per numbered or bulleted line; unmarked lines continue the step above. */
export function splitSteps(text: string): string[] {
  const steps: string[] = [];
  for (const line of text.split('\n')) {
    if (!line.trim()) continue;
    const s = stepText(line);
    if (s !== null) steps.push(s);
    else if (steps.length) steps[steps.length - 1] = `${steps[steps.length - 1]} ${line.trim()}`;
    else steps.push(line.trim());
  }
  return steps;
}

export function splitArticle(body: string): ArticleParts {
  const format = formatOf(body);
  // Only the article's own format's headings are sections; the other's are
  // kept as written in `other`, so nothing is silently re-filed.
  const byHeading = new Map<string, SectionKey>(sectionsFor(format).map((s) => [s.heading.toLowerCase(), s.key]));
  const parts = emptyParts(format);
  const other: string[] = [];
  let current: { key: SectionKey | null; heading: string | null; lines: string[] } = { key: null, heading: null, lines: [] };
  const flush = () => {
    const text = current.lines.join('\n').trim();
    if (current.key === 'steps') parts.steps.push(...splitSteps(text));
    else if (current.key) {
      const k = current.key;
      parts[k] = parts[k] ? `${parts[k]}\n\n${text}` : text;
    } else if (current.heading !== null || text) {
      other.push(current.heading !== null ? `## ${current.heading}\n${text}`.trim() : text);
    }
  };
  for (const line of (body ?? '').replace(/\r\n/g, '\n').split('\n')) {
    const h = /^#{1,6}\s+(.+?)\s*#*\s*$/.exec(line);
    if (h) {
      flush();
      const heading = h[1]!.trim();
      current = { key: byHeading.get(heading.toLowerCase()) ?? null, heading, lines: [] };
    } else {
      current.lines.push(line);
    }
  }
  flush();
  parts.other = other.filter(Boolean).join('\n\n');
  return parts;
}

/** The stored body: the format's sections in house order, empty ones left out, then anything else. */
export function joinArticle(parts: ArticleParts): string {
  const out: string[] = [];
  for (const s of sectionsFor(parts.format)) {
    if (s.key === 'steps') {
      const steps = parts.steps.map((x) => x.trim()).filter(Boolean);
      if (steps.length) out.push(`## Steps\n${steps.map((x, i) => `${i + 1}. ${x}`).join('\n')}`);
      continue;
    }
    const text = parts[s.key].trim();
    if (text) out.push(`## ${s.heading}\n${text}`);
  }
  if (parts.other.trim()) out.push(parts.other.trim());
  return out.join('\n\n');
}

/**
 * The same article in the other format, for the editor's switch.
 *
 * Moves what has an obvious home — Symptom and Question, Steps and Answer,
 * "If that did not work" and More information — and puts anything without
 * one into `other`, so switching back and forth never loses a word.
 */
export function switchFormat(parts: ArticleParts, to: ArticleFormat): ArticleParts {
  if (parts.format === to) return parts;
  const keep = (...xs: string[]) => xs.map((x) => x.trim()).filter(Boolean).join('\n\n');
  if (to === 'information') {
    const steps = parts.steps.map((x) => x.trim()).filter(Boolean);
    return {
      ...emptyParts('information'),
      question: parts.symptom,
      appliesTo: parts.appliesTo,
      answer: keep(steps.map((x, i) => `${i + 1}. ${x}`).join('\n'), parts.check),
      more: parts.escalation,
      other: parts.other,
    };
  }
  // An answer written as a list is steps; prose has no obvious home and is kept in `other`.
  const lines = parts.answer.split('\n').filter((l) => l.trim());
  const isList = lines.length > 0 && lines.every((l) => /^\s*(?:\d+[.)]|[-*•])\s+/.test(l));
  return {
    ...emptyParts('troubleshooting'),
    symptom: parts.question,
    appliesTo: parts.appliesTo,
    steps: isList ? splitSteps(parts.answer) : [],
    escalation: parts.more,
    other: keep(isList ? '' : parts.answer, parts.other),
  };
}
